/**
 * User Profile Hook
 *
 * Fetches the user's full profile including their organization's industry.
 * Automatically sets the industry in IndustryContext when available.
 */

import { useEffect, useCallback, useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useIndustry } from '@/contexts/IndustryContext'
import { get } from '@/services/api'
import { IndustryId } from '@/config/industryTypes'

interface UserProfile {
  username: string
  email?: string
  first_name?: string
  last_name?: string
  roles: string[]
  organization_id?: string
  organization_name?: string
  industry?: string
}

export function useUserProfile() {
  const { isAuthenticated, getToken } = useAuth()
  const { setIndustry, industryId } = useIndustry()
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchProfile = useCallback(async () => {
    const token = getToken()
    if (!token) return

    setIsLoading(true)
    setError(null)

    try {
      const data = await get<UserProfile>('/auth/me')

      setProfile(data)

      // If user has an organization with an industry, set it
      if (data.industry) {
        // Check if this is a valid industry ID
        const validIndustries: IndustryId[] = [
          'manufacturing', 'oil_gas', 'healthcare', 'financial',
          'energy', 'transportation', 'mining', 'chemicals',
          'food_beverage', 'pharmaceuticals', 'aerospace', 'metals',
          'iot_telecom', 'utilities', 'pulp_paper', 'automotive',
          'datacenter', 'facility_management',
        ]

        if (validIndustries.includes(data.industry as IndustryId)) {
          // Only update if different from current to avoid unnecessary re-renders
          if (data.industry !== industryId) {
            console.log(`[UserProfile] Setting industry from organization: ${data.industry}`)
            setIndustry(data.industry as IndustryId)
          }
        } else {
          console.warn(`[UserProfile] Invalid industry from organization: ${data.industry}`)
        }
      }
    } catch (err) {
      console.error('Failed to fetch user profile:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch profile')
    } finally {
      setIsLoading(false)
    }
  }, [getToken, setIndustry, industryId])

  // Fetch profile when user becomes authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchProfile()
    } else {
      setProfile(null)
    }
  }, [isAuthenticated, fetchProfile])

  return {
    profile,
    isLoading,
    error,
    refetchProfile: fetchProfile,
  }
}

export default useUserProfile
